import { CSSProperties, memo, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Widget } from "../../../Services";
import { colors, fonts } from "../../../Styles";
import { CheckBox, CheckStyles, NewInput, TooltipString } from "../..";
import { NewFieldStyles, NewFieldTypes } from "../../../Definitions";

interface AttentionBar_Props {
  widget?: Widget;
  onCheck?: () => void | undefined;
  onChange?: (widget: Widget) => void | undefined;
  borderRadius?: CSSProperties;
  headerBar?: boolean;
  deleteable?: boolean;
  isChecked?: boolean;
  deletable?: boolean;
  editable?: boolean
}
/**
 * PEN_BASIC
 * @param attention - A single attention log entry containing event details.
 * @param onCheck - Callback triggered when the checkbox is toggled (if `selectable` is `true`).
 * @param borderRadius - Optional custom border radius for the outer container (e.g., `{ borderRadius: "6px" }`).
 * @param headerBar - If `true`, displays column headers instead of actual data.
 * @param attentionColor - Optional color used for the left border to indicate severity or type (e.g., `colors.red`).
 * @param deletable - If `true`, shows a checkbox at the beginning of the row.
 * @param isChecked - Indicates whether the checkbox should appear as checked.
 * @param editable - If `true`, values can be changed.
 * @returns A styled row element displaying either header labels or formatted attention data with tooltips.
 */
function WIDGET_BAR({ widget, onCheck, borderRadius, headerBar=false, deletable=false, isChecked=false, onChange, editable }: AttentionBar_Props) {
  const { t } = useTranslation();

  // Widget Type Options
  const widgetTypeOptions = [
    { label: t("widgets.general.autoReports", {defaultValue: "Auto Reports"}), id: 1 },
    { label: t("widgets.general.autoReportsAndMore", {defaultValue: "Auto Reports etc."}), id: 2 },
    { label: t("widgets.general.graph", {defaultValue: "Graph"}), id: 3 },
    { label: t("widgets.general.progress", {defaultValue: "Progress Circles"}), id: 4}
  ]

  const widgetType = widgetTypeOptions?.find(w => w.id === widget?.type)?.label ?? ""
  const widgetNameRef = useRef<string | undefined>(widget?.name);
  const widgetPriorityRef = useRef<number | undefined>(widget?.display_order);

  // Translations
  const translations = useMemo(() => ({
    widgetName: t("widgets.general.name", { defaultValue: "Name" }),
    widgetPriority: t("widgets.general.widgetPriority", { defaultValue: "Priority" }),
    widgetType: t("widgets.general.type", { defaultValue: "Type" })
  }), [t]);

  // Columns
  const columns = useMemo(() => [
    {
      key: "widgetName", content: headerBar
        ? translations.widgetName
        : editable
          ? <NewInput
              onChange={(e) => {if (widget && onChange && widgetPriorityRef.current !== undefined) {widgetNameRef.current = e; onChange({...widget, name: e, display_order: widgetPriorityRef.current})}}}
              valueOverrides={{
                defaultValue: widget?.name,
                valueType: "string"    
              }}
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greyTertiary, 
                fieldType: NewFieldTypes.tertiaryField, 
                fieldWidth: "100%",
              }}
            />
          : widget?.name,
    },
    {
      key: "widgetPriority", content: headerBar
        ? translations.widgetPriority
        : editable
          ? <NewInput
              onChange={(e) => {if (widget && onChange && widgetNameRef.current !== undefined) {widgetPriorityRef.current = Number(e); onChange({...widget, name: widgetNameRef.current, display_order: Number(e)})}}}
              valueOverrides={{
                defaultValue: widget?.display_order,
                valueType: "number"    
              }}
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greyTertiary, 
                fieldType: NewFieldTypes.tertiaryField, 
                fieldWidth: "100%",
              }}
            />
          : widget?.display_order
    },
    { key: "widgetType", content: headerBar ? translations.widgetType : widgetType }
  ], [headerBar, translations.widgetName, translations.widgetPriority, translations.widgetType, editable, widget, widgetType, onChange]);

  if (!widget && !headerBar) return null;

  return (
    <div style={{ height: "35px", width: "650px", boxSizing: "border-box", zIndex: "99999", position: "relative" }}>
      <div style={{
        width: "100%",
        height: "100%",
        textAlign: "center",
        alignContent: "center",
        padding: "0px 20px",
        boxSizing: "border-box",
        backgroundColor: colors.greyLightSecondary,
        transition: "filter 0.3s",
        color: colors.greyDarkPrimary,
        ...fonts.paragraph,
        ...fonts.fontSize4,
        ...borderRadius,
        zIndex: "99999",
        position: "relative",
      }}>
        <div style={{ display: "grid", boxSizing: "border-box", height: "35px", gridTemplateColumns: "22px 356px 65px 125px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
          {deletable ? <CheckBox checkStyle={CheckStyles.transparent} isChecked={isChecked} onChange={onCheck}/> : <span/>}
          {columns.map((col) => (
            headerBar ? (
              <TooltipString
                key={col.key}
                tooltipString={translations[col.key as keyof typeof translations]}
                contentString={String(col.content)}
              />
            ) : (
            <p key={col.key} style={{...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary}}>
              {col.content}
            </p>
          )))}
          <span></span>
        </div>
      </div>
    </div>
  );
}

export default memo(WIDGET_BAR);